import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../components/ui/Button';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiCheckCircle, FiAlertCircle, FiCheck, FiZap, FiUsers } = FiIcons;
const { FaWhatsapp } = FaIcons;

const TargetItem = ({ title }) => (
  <div className="flex items-center gap-3 p-5 bg-white border border-gray-100 rounded-2xl shadow-sm hover:shadow-md transition-shadow">
    <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 shrink-0">
      <SafeIcon icon={FiCheck} className="w-5 h-5" />
    </div>
    <span className="font-bold text-gray-800 text-lg">{title}</span>
  </div>
);

const Home = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* 1. HERO SECTION */}
      <section className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto text-center border-b border-gray-50">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl sm:text-7xl font-extrabold text-gray-900 tracking-tight mb-6 leading-[1.1]">
            Sell Your Domains Faster <br />
            <span className="text-indigo-600">With AI Sales Pages</span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto font-medium">
            Turn your parked domains into high-converting sales pages in 30 seconds.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Link to="/pricing">
              <Button className="h-16 px-10 text-xl font-bold shadow-2xl shadow-indigo-200 rounded-2xl">
                Buy Lifetime Access - $29
              </Button>
            </Link>
            <Link to="/signup">
              <Button variant="secondary" className="h-16 px-10 text-xl font-bold rounded-2xl">
                Try Free Demo
              </Button>
            </Link>
          </div>

          {/* 2. WHATSAPP BUTTON */}
          <div className="flex flex-col items-center gap-4">
            <a 
              href="https://wa.me/966XXXXXXXXX" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white rounded-full font-bold text-lg hover:bg-[#1DA851] transition-all shadow-xl hover:scale-105 active:scale-95"
            >
              <SafeIcon icon={FaWhatsapp} className="w-6 h-6" />
              Contact on WhatsApp
            </a>
            
            {/* 6. URGENCY */}
            <p className="text-sm font-bold text-red-500 flex items-center gap-2 animate-pulse">
              <SafeIcon icon={FiZap} className="w-4 h-4" />
              Only for first 100 users
            </p>
          </div>
        </motion.div>
      </section>

      {/* 4. PROOF SECTION: See the Difference */}
      <section className="py-24 bg-gray-50/50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">See the Difference</h2>
            <p className="text-xl text-gray-500">Transform your dead traffic into active leads.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Before */}
            <div className="relative">
              <div className="absolute -top-4 -left-4 bg-red-500 text-white px-4 py-1 rounded-lg font-bold text-sm z-10 shadow-lg">BEFORE</div>
              <div className="bg-white rounded-3xl border-2 border-dashed border-gray-200 p-8 grayscale opacity-70">
                <div className="h-4 w-1/2 bg-gray-200 rounded mb-4"></div>
                <div className="h-32 bg-gray-100 rounded-2xl flex items-center justify-center mb-4">
                   <SafeIcon icon={FiAlertCircle} className="w-12 h-12 text-gray-300" />
                </div>
                <p className="text-gray-400 font-bold text-center text-xl">Under Construction</p>
                <div className="mt-8 space-y-2">
                  <div className="h-2 bg-gray-100 rounded w-full"></div>
                  <div className="h-2 bg-gray-100 rounded w-3/4"></div>
                </div>
              </div>
            </div>

            {/* After */}
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-indigo-600 text-white px-4 py-1 rounded-lg font-bold text-sm z-10 shadow-lg">AFTER MOLOJI</div>
              <div className="bg-white rounded-3xl border-4 border-indigo-600 p-8 shadow-2xl scale-105">
                <div className="flex items-center gap-2 mb-6">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                </div>
                <h3 className="text-3xl font-black text-gray-900 mb-2">PremiumAsset.com</h3>
                <span className="inline-block bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold mb-6">AVAILABLE FOR PURCHASE</span>
                <div className="space-y-4">
                  <div className="h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white font-bold">Contact Seller</div>
                  <div className="h-10 border-2 border-indigo-600 rounded-xl flex items-center justify-center text-indigo-600 font-bold">Make Offer</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. TARGET USERS SECTION */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-extrabold text-gray-900 mb-4">Who Is This For?</h2>
            <p className="text-lg text-gray-500">Designed for those who take domain investing seriously.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-6">
            <TargetItem title="Domain Investors" />
            <TargetItem title="Domain Flippers" />
            <TargetItem title="Afternic & Sedo Sellers" />
            <TargetItem title="Portfolio Owners" />
          </div>
        </div>
      </section>

      {/* 3. OFFER SECTION (Price Update) */}
      <section className="py-24 bg-indigo-900 text-white overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <div className="inline-block px-4 py-1 bg-indigo-500/30 border border-indigo-400/50 rounded-full text-indigo-200 text-sm font-bold mb-6">
            LIFETIME DEAL
          </div>
          <h2 className="text-5xl font-black mb-6">Limited Lifetime Deal</h2>
          <p className="text-xl text-indigo-100 mb-12 max-w-2xl mx-auto leading-relaxed">
            Get unlimited domain landing pages for a one-time payment of $29. 
            No monthly fees, ever.
          </p>
          
          <div className="bg-white rounded-[2.5rem] p-12 text-gray-900 shadow-2xl max-w-lg mx-auto relative">
            <div className="absolute -top-6 left-1/2 -translate-x-1/2 bg-yellow-400 text-black font-black px-6 py-2 rounded-xl rotate-2">
              BEST VALUE
            </div>
            <div className="flex items-baseline justify-center gap-2 mb-8">
              <span className="text-7xl font-black tracking-tighter">$29</span>
              <span className="text-gray-400 text-xl font-medium">/ lifetime</span>
            </div>
            
            <ul className="text-left space-y-4 mb-10">
              {["Unlimited domains", "AI-powered descriptions", "WhatsApp integration", "Custom contact forms", "Instant publishing"].map(f => (
                <li key={f} className="flex items-center gap-3 font-bold text-gray-700">
                  <SafeIcon icon={FiCheckCircle} className="text-green-500 w-6 h-6" />
                  {f}
                </li>
              ))}
            </ul>

            <Link to="/signup">
              <Button className="w-full h-16 text-xl font-bold shadow-xl shadow-indigo-100 rounded-2xl">
                Get Lifetime Access Now
              </Button>
            </Link>
            {/* 6. URGENCY */}
            <p className="mt-4 text-sm font-bold text-red-500 uppercase tracking-widest">
              Only for first 100 users
            </p>
          </div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 bg-white text-center px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-5xl font-black text-gray-900 mb-8">
            Start Selling Your Domains Smarter Today
          </h2>
          <Link to="/signup">
            <Button className="h-20 px-12 text-2xl font-bold shadow-2xl shadow-indigo-300 rounded-[2rem] hover:scale-105 transition-transform">
              Get Lifetime Access - $29
            </Button>
          </Link>
          <p className="mt-8 text-gray-400 font-medium">
            Join 500+ domainers who already sold their first domain using Moloji.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100 text-center">
        <p className="text-gray-400 text-sm font-medium">
          © {new Date().getFullYear()} Moloji. Built for people who sell domains.
        </p>
      </footer>
    </div>
  );
};

export default Home;